<!-- eslint-disable vue/no-mutating-props -->
<template>
  <v-card
    v-if="mobile"
    elevation="0"
    color="grey-lighten-2 text-on-surface"
    class="mx-2 my-2"
  >
    <v-card-text>
      <card-item
        :title="$t('entities.type')"
        style="margin-bottom: 0.5rem !important"
        :content="typeLabel"
        bold
      />
      <card-item title="Nome do Portador" style="margin-bottom: 0.5rem !important">
        <t-text-input
          v-model="item.name"
          density="compact"
          :rules="[ rules.nameFormat, rules.nameLength ]"
          required
          class="mb-2"
        />
      </card-item>
      <card-item
        title="Documento"
        style="margin-bottom: 0.5rem !important"
        :no-divider="item.additional.length === 0"
        class="ga-3"
      >
        <t-select
          v-model="item.document_type"
          label="Tipo do Documento"
          :items="documentTypeItems"
          variant="outlined"
          density="compact"
          @update:model-value="item.cpf = ''"
        />
        <t-document-input
          v-model="item.cpf"
          :document-type="item.document_type"
          density="compact"
          required
          class="pb-1"
          no-label
        />
      </card-item>
      <card-item
        v-for="(additional, j) in item.additional.sort((a, b) => a.field.order - b.field.order)"
        :key="additional.field.id"
        :title="additional.field.label"
        style="margin-bottom: 0.5rem !important"
        :no-divider="j === item.additional.length - 1"
      >
        <additional-field-input :additional="additional" />
      </card-item>
      <card-item v-if="events.hasBiometricAccess">
        <validation-ticket-item-biometry ref="biometry" :ticket="item" />
      </card-item>
    </v-card-text>
  </v-card>

  <tr v-else>
    <v-row class="mx-0 my-0 mt-6">
      <v-col :cols="calculateFieldSize(0)">
        <t-text-input
          :model-value="typeLabel"
          :label="$t('entities.type')"
          density="compact"
          disabled
        />
      </v-col>

      <v-col :cols="calculateFieldSize(1)">
        <t-text-input
          v-model="item.name"
          label="Nome do Portador"
          density="compact"
          :rules="[ rules.nameFormat, rules.nameLength ]"
          required
        />
      </v-col>

      <v-col :cols="calculateFieldSize(2)">
        <t-select
          v-model="item.document_type"
          label="Tipo do Documento"
          :items="documentTypeItems"
          variant="outlined"
          density="compact"
          @update:model-value="item.cpf = ''"
        />
      </v-col>

      <v-col :cols="calculateFieldSize(3)" class="d-flex align-center">
        <t-document-input
          v-model="item.cpf"
          :document-type="item.document_type"
          density="compact"
          required
        />
      </v-col>

      <v-col
        v-for="(additional, j) in item.additional.sort((a, b) => a.field.order - b.field.order)"
        :key="additional.field.id"
        :cols="calculateFieldSize(defaultFieldCount + j)"
      >
        <additional-field-input :additional="additional" :label="additional.field.label" />
      </v-col>

      <v-col v-if="events.hasBiometricAccess" :cols="calculateFieldSize(defaultFieldCount + item.additional.length)">
        <validation-ticket-item-biometry ref="biometry" :ticket="item" />
      </v-col>
    </v-row>
    <hr v-if="!last">
  </tr>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import { useDisplay } from "vuetify";

import type NominalTicket from "@/types/NominalTicket";
import TDocumentInput from "../core/TDocumentInput.vue";
import TTextInput from "../core/TTextInput.vue";
import TSelect from "../core/TSelect.vue";
import AdditionalFieldInput from "./AdditionalFieldInput.vue";
import ValidationTicketItemBiometry from "./ValidationTicketItemBiometry.vue";
import CardItem from "./MobileCardItem.vue";
import { useEventsStore } from "@/stores/events";
import { personName } from "@/utils/name";

interface Props {
  item: NominalTicket;
  last: boolean;
}

const events = useEventsStore();

const { item, last = false } = defineProps<Props>();

defineOptions({
  name: "ValidationTicketItem",
});

const { mobile } = useDisplay();

/** A seat ticket shows its seat next to its type, as "Inteira · Lugar A12". */
const typeLabel = computed<string>(() => item.seat ? `${item.type.name} · Lugar ${item.seat.full_label}` : item.type.name);

const biometry = useTemplateRef<{ validate: () => boolean } | null>("biometry");

const documentTypeItems = [
  { title: "CPF", value: "cpf" },
  { title: "Passaporte", value: "passport" },
];

// Validation rules
const rules = {
  nameFormat: (v?: string): boolean | string => !v || personName.validate(v),
  nameLength: (v?: string): boolean | string => !v || v.length > 2 || "O nome deve conter ao menos 3 letras",
};

// Methods
const validate = (): boolean => {
  if (!events.hasBiometricAccess) {
    return true;
  };

  return biometry.value?.validate?.() ?? true;
};

const defaultFieldCount = 4;

const calculateFieldSize = (index: number): number => {
  const fieldCount = defaultFieldCount + item.additional.length + (events.hasBiometricAccess ? 1 : 0);

  if (fieldCount <= 4) {
    return 12 / fieldCount;
  }

  const remainder = fieldCount % 4;
  const isInLastRow = index >= fieldCount - remainder;

  if (remainder !== 0 && isInLastRow) {
    return 12 / remainder;
  }

  return 3;
};

// Expose methods to parent component
defineExpose({
  validate,
});
</script>
